import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/axios';
import { FaArrowLeft, FaEye, FaFilter, FaSearch } from 'react-icons/fa';

const FreezeHistoryPage = () => {
  const navigate = useNavigate();
  const [histories, setHistories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0
  });
  const [filters, setFilters] = useState({
    productId: '',
    userId: '',
    actionType: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchHistories();
  }, [pagination.currentPage, filters]);

  const fetchHistories = async () => {
    try {
      setIsLoading(true);
      const params = {
        page: pagination.currentPage,
        limit: 20,
        ...filters
      };
      
      const response = await api.get('/freeze-histories', { params });
      setHistories(response.data.histories);
      setPagination(prev => ({
        ...prev,
        totalPages: response.data.totalPages,
        totalCount: response.data.totalCount
      }));
    } catch (error) {
      console.error('Error fetching freeze histories:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      productId: '',
      userId: '',
      actionType: '',
      search: ''
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionColor = (actionType) => {
    switch (actionType) {
      case 'freeze': return '#10b981';
      case 'extend': return '#f59e0b';
      case 'release': return '#ef4444';
      case 'expire': return '#6b7280';
      case 'modify': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'freeze': return '❄️';
      case 'extend': return '⏰';
      case 'release': return '🔓';
      case 'expire': return '⏳';
      case 'modify': return '✏️';
      default: return '📝';
    }
  };

  const refresh = () => setPagination(p => ({ ...p }));

  const extendExpiry = async (history) => {
    const fqId = history?.frozenQuantity?.id;
    if (!fqId) return;
    const days = prompt('Extend by days (e.g., 7):', '7');
    if (!days) return;
    const newDate = new Date(history.frozenQuantity.expires_at || Date.now());
    newDate.setDate(newDate.getDate() + Number(days));
    try {
      await api.put(`/frozen-quantities/update-expiry/${fqId}`, { expires_at: newDate.toISOString().slice(0,16) });
      await fetchHistories();
    } catch (e) { console.error(e); }
  };

  const modifyQuantity = async (history) => {
    const fqId = history?.frozenQuantity?.id;
    if (!fqId) return;
    const current = history?.frozenQuantity?.quantity || history.quantity;
    const nextQtyStr = prompt('Set new frozen quantity:', String(current));
    if (!nextQtyStr) return;
    const nextQty = Number(nextQtyStr);
    if (!Number.isFinite(nextQty) || nextQty <= 0) return;
    try {
      await api.put(`/frozen-quantities/update-quantity/${fqId}`, { quantity: nextQty });
      await fetchHistories();
    } catch (e) { console.error(e); }
  };

  return (
    <div style={{ background: '#fff', minHeight: '100vh', paddingBottom: 74 }}>
      {/* Header */}
      <div style={{
        padding: '18px 20px 0 20px',
        background: '#fff',
        position: 'sticky',
        top: 0,
        zIndex: 9,
        borderBottom: '1px solid #f0f0f0'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={() => navigate('/admin/products')}
              style={{
                background: 'none',
                border: 'none',
                padding: 8,
                cursor: 'pointer',
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <FaArrowLeft size={20} color="#333" />
            </button>
            <div>
              <h1 style={{
                fontSize: 24,
                fontWeight: 700,
                color: '#333',
                margin: 0
              }}>
                Freeze History
              </h1>
              <p style={{
                fontSize: 14,
                color: '#666',
                margin: '4px 0 0 0'
              }}>
                Track all freeze quantity actions
              </p>
            </div>
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: showFilters ? '#333' : '#f5f5f5',
              color: showFilters ? '#fff' : '#333',
              border: '1px solid #ddd',
              borderRadius: 8,
              padding: '8px 16px',
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <FaFilter size={14} />
            Filters
          </button>
        </div>

        {/* Search Bar */}
        <div style={{ marginBottom: 16 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            background: '#fff',
            borderRadius: 12,
            border: '2px solid #e0e0e0',
            padding: '8px 16px',
            transition: 'border-color 0.2s ease'
          }}>
            <FaSearch size={16} color="#888" style={{ marginRight: 12 }} />
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search by product name, user name, or action..."
              style={{
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 15,
                color: '#222',
                flex: 1,
                padding: 4
              }}
            />
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div style={{
            background: '#f8f9fa',
            borderRadius: 12,
            padding: 16,
            marginBottom: 16,
            border: '1px solid #e9ecef'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 16,
              marginBottom: 16
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: 14,
                  fontWeight: 500,
                  color: '#333',
                  marginBottom: 8
                }}>
                  Action Type
                </label>
                <select
                  name="actionType"
                  value={filters.actionType}
                  onChange={handleFilterChange}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    fontSize: 14,
                    background: '#fff'
                  }}
                >
                  <option value="">All Actions</option>
                  <option value="freeze">Freeze</option>
                  <option value="extend">Extend</option>
                  <option value="release">Release</option>
                  <option value="expire">Expire</option>
                  <option value="modify">Modify</option>
                </select>
              </div>
            </div>
            
            <div style={{
              display: 'flex',
              gap: 12,
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={clearFilters}
                style={{
                  padding: '8px 16px',
                  background: '#f5f5f5',
                  color: '#666',
                  border: '1px solid #ddd',
                  borderRadius: 6,
                  fontSize: 14,
                  cursor: 'pointer'
                }}
              >
                Clear
              </button>
              <button
                onClick={() => setShowFilters(false)}
                style={{
                  padding: '8px 16px',
                  background: '#333',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 14,
                  cursor: 'pointer'
                }}
              >
                Apply
              </button>
            </div>
          </div>
        )}

        {/* Stats */}
        <div style={{
          display: 'flex',
          gap: 16,
          marginBottom: 16
        }}>
          <div style={{
            background: '#f0f9ff',
            padding: '12px 16px',
            borderRadius: 8,
            border: '1px solid #bae6fd'
          }}>
            <div style={{ fontSize: 12, color: '#0369a1', fontWeight: 500 }}>Total Actions</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#0c4a6e' }}>{pagination.totalCount}</div>
          </div>
        </div>
      </div>

      {/* History List */}
      <div style={{ padding: '0 20px' }}>
        {isLoading ? (
          <div style={{
            textAlign: 'center',
            padding: 40,
            color: '#666'
          }}>
            Loading history...
          </div>
        ) : histories.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: 40,
            color: '#666'
          }}>
            No history found
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {histories.map((history) => (
              <div
                key={history.id}
                style={{
                  background: '#fff',
                  borderRadius: 10,
                  padding: 12,
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.06)'
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  marginBottom: 8
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      fontSize: 20,
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: '#f3f4f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {getActionIcon(history.action_type)}
                    </div>
                    <div>
                      <div style={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: '#333',
                        marginBottom: 2
                      }}>
                        {history.product?.name || 'Unknown Product'}
                      </div>
                      <div style={{
                        fontSize: 12,
                        color: '#666'
                      }}>
                        {history.user?.name || 'Guest User'}
                        {history.user?.email && (
                          <span style={{ marginLeft: 8, color: '#999' }}>
                            ({history.user.email})
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div style={{
                    padding: '3px 10px',
                    borderRadius: 16,
                    background: getActionColor(history.action_type),
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase'
                  }}>
                    {history.action_type}
                  </div>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                  gap: 10,
                  marginBottom: 8
                }}>
                  <div>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Quantity</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#333' }}>
                      {history.quantity} units
                    </div>
                  </div>
                  
                  <div>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Date</div>
                    <div style={{ fontSize: 13, color: '#333' }}>
                      {formatDate(history.action_date)}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Expires</div>
                    <div style={{ fontSize: 13, color: '#333' }}>
                      {history.frozenQuantity?.expires_at ? formatDate(history.frozenQuantity.expires_at) : '-'}
                    </div>
                  </div>
                  
                  <div>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Performed By</div>
                    <div style={{ fontSize: 13, color: '#333' }}>
                      {history.performed_by === 'admin' ? 'Admin' : 'User'}
                    </div>
                  </div>
                </div>

                {(history.client_name || history.architect_name || history.remarks) && (
                  <div style={{
                    background: '#f8f9fa',
                    borderRadius: 8,
                    padding: 10,
                    marginBottom: 8
                  }}>
                    {history.client_name && (
                      <div style={{ marginBottom: 6 }}>
                        <span style={{ fontSize: 12, color: '#666', fontWeight: 500 }}>Client: </span>
                        <span style={{ fontSize: 14, color: '#333' }}>{history.client_name}</span>
                      </div>
                    )}
                    {history.architect_name && (
                      <div style={{ marginBottom: 6 }}>
                        <span style={{ fontSize: 12, color: '#666', fontWeight: 500 }}>Architect: </span>
                        <span style={{ fontSize: 14, color: '#333' }}>{history.architect_name}</span>
                      </div>
                    )}
                    {history.remarks && (
                      <div>
                        <span style={{ fontSize: 12, color: '#666', fontWeight: 500 }}>Remarks: </span>
                        <span style={{ fontSize: 13, color: '#333' }}>{history.remarks}</span>
                      </div>
                    )}
                  </div>
                )}

                {history.previous_expiry && history.new_expiry && (
                  <div style={{
                    background: '#fef3c7',
                    borderRadius: 8,
                    padding: 10,
                    border: '1px solid #f59e0b'
                  }}>
                    <div style={{ fontSize: 12, color: '#92400e', fontWeight: 500, marginBottom: 4 }}>
                      Expiry Changed
                    </div>
                    <div style={{ fontSize: 14, color: '#92400e' }}>
                      From: {formatDate(history.previous_expiry)} → To: {formatDate(history.new_expiry)}
                    </div>
                  </div>
                )}

                {/* Actions: Extend / Modify */}
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button onClick={() => extendExpiry(history)} style={{ padding: '6px 10px', borderRadius: 6, background: '#2563eb', color: '#fff', border: 'none', fontSize: 12, cursor: 'pointer' }}>Extend</button>
                  <button onClick={() => modifyQuantity(history)} style={{ padding: '6px 10px', borderRadius: 6, background: '#111827', color: '#fff', border: 'none', fontSize: 12, cursor: 'pointer' }}>Modify</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 8,
            marginTop: 24,
            padding: '16px 0'
          }}>
            <button
              onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.max(1, prev.currentPage - 1) }))}
              disabled={pagination.currentPage === 1}
              style={{
                padding: '8px 16px',
                background: pagination.currentPage === 1 ? '#f5f5f5' : '#333',
                color: pagination.currentPage === 1 ? '#999' : '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: pagination.currentPage === 1 ? 'not-allowed' : 'pointer',
                fontSize: 14
              }}
            >
              Previous
            </button>
            
            <span style={{
              padding: '8px 16px',
              background: '#f5f5f5',
              borderRadius: 6,
              fontSize: 14,
              color: '#333'
            }}>
              {pagination.currentPage} of {pagination.totalPages}
            </span>
            
            <button
              onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.min(prev.totalPages, prev.currentPage + 1) }))}
              disabled={pagination.currentPage === pagination.totalPages}
              style={{
                padding: '8px 16px',
                background: pagination.currentPage === pagination.totalPages ? '#f5f5f5' : '#333',
                color: pagination.currentPage === pagination.totalPages ? '#999' : '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: pagination.currentPage === pagination.totalPages ? 'not-allowed' : 'pointer',
                fontSize: 14
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FreezeHistoryPage;
